// Move generation reads abilities, never piece kind. A pawn that learned
// knight jumps is just a piece with two abilities here. Terrain and the
// piece's conditions filter what the abilities offer.

import { effectiveAbilities } from './abilities';
import { isSide } from './pieces';
import { pushTarget } from './push';
import { fileOf, forwardDirection, isOnBoard, pawnStartRank, rankOf, squareIndex } from './squares';
import { isSlow, isSolid } from './terrain';
import type { Move, Piece, Position, Rules, Side } from './types';

type Offset = readonly [number, number];

const KNIGHT_OFFSETS: readonly Offset[] = [
  [1, 2],
  [2, 1],
  [2, -1],
  [1, -2],
  [-1, -2],
  [-2, -1],
  [-2, 1],
  [-1, 2],
];

const KING_OFFSETS: readonly Offset[] = [
  [-1, -1],
  [0, -1],
  [1, -1],
  [-1, 0],
  [1, 0],
  [-1, 1],
  [0, 1],
  [1, 1],
];

const HOP_OFFSETS: readonly Offset[] = KING_OFFSETS.map(([df, dr]) => [df * 2, dr * 2]);

const BISHOP_DIRECTIONS: readonly Offset[] = [
  [1, 1],
  [1, -1],
  [-1, 1],
  [-1, -1],
];

const ROOK_DIRECTIONS: readonly Offset[] = [
  [1, 0],
  [-1, 0],
  [0, 1],
  [0, -1],
];

/** May the mover go from `from` to the square: empty, a push, or a capture. */
const canLandOn = (position: Position, from: number, square: number, mover: Piece): boolean => {
  if (isSolid(position.rules, square)) return false;
  const occupant = position.board[square];
  if (!occupant) return true;
  if (pushTarget(position, { from, to: square }) !== null) return true;
  if (occupant.owner === mover.owner) return false;
  return occupant.uncapturableTurns === 0;
};

const addLeaps = (
  position: Position,
  from: number,
  mover: Piece,
  offsets: readonly Offset[],
  out: Move[],
) => {
  const file = fileOf(from);
  const rank = rankOf(from);
  for (const [df, dr] of offsets) {
    if (!isOnBoard(file + df, rank + dr)) continue;
    const to = squareIndex(file + df, rank + dr);
    if (canLandOn(position, from, to, mover)) out.push({ from, to });
  }
};

const addSlides = (
  position: Position,
  from: number,
  mover: Piece,
  directions: readonly Offset[],
  out: Move[],
) => {
  const maxSteps = mover.shortSlides ? 1 : Number.POSITIVE_INFINITY;
  const passThrough = mover.passThroughTurns > 0;
  const file = fileOf(from);
  const rank = rankOf(from);
  for (const [df, dr] of directions) {
    let steps = 1;
    while (steps <= maxSteps && isOnBoard(file + df * steps, rank + dr * steps)) {
      const to = squareIndex(file + df * steps, rank + dr * steps);
      if (isSolid(position.rules, to)) break;
      const occupant = position.board[to];
      if (!occupant) {
        out.push({ from, to });
      } else {
        if (canLandOn(position, from, to, mover)) out.push({ from, to });
        if (!passThrough) break;
      }
      steps += 1;
    }
  }
};

/** Slides to empty squares. Captures only by hopping exactly one piece. */
const addCannon = (position: Position, from: number, mover: Piece, out: Move[]) => {
  const file = fileOf(from);
  const rank = rankOf(from);
  for (const [df, dr] of ROOK_DIRECTIONS) {
    let steps = 1;
    let hopped = false;
    while (isOnBoard(file + df * steps, rank + dr * steps)) {
      const to = squareIndex(file + df * steps, rank + dr * steps);
      if (isSolid(position.rules, to)) break;
      const occupant = position.board[to];
      if (!hopped) {
        if (!occupant) out.push({ from, to });
        else hopped = true;
      } else if (occupant) {
        if (canLandOn(position, from, to, mover)) out.push({ from, to });
        break;
      }
      steps += 1;
    }
  }
};

const addPawnMoves = (position: Position, from: number, mover: Piece, side: Side, out: Move[]) => {
  const { board, rules } = position;
  const file = fileOf(from);
  const rank = rankOf(from);
  const directions = rules.pawnsRetreat
    ? [forwardDirection(side), -forwardDirection(side)]
    : [forwardDirection(side)];

  for (const dir of directions) {
    if (!isOnBoard(file, rank + dir)) continue;
    const oneAhead = squareIndex(file, rank + dir);
    if (board[oneAhead] === null && !isSolid(rules, oneAhead)) {
      out.push({ from, to: oneAhead });
      const onStartRank = rank === pawnStartRank(side) && dir === forwardDirection(side);
      if (!mover.moved && onStartRank && isOnBoard(file, rank + dir * 2)) {
        const twoAhead = squareIndex(file, rank + dir * 2);
        if (board[twoAhead] === null && !isSolid(rules, twoAhead)) out.push({ from, to: twoAhead });
      }
    } else if (pushTarget(position, { from, to: oneAhead }) !== null) {
      out.push({ from, to: oneAhead });
    }
    for (const df of [-1, 1]) {
      if (!isOnBoard(file + df, rank + dir)) continue;
      const diagonal = squareIndex(file + df, rank + dir);
      const occupant = board[diagonal];
      if (occupant && canLandOn(position, from, diagonal, mover)) out.push({ from, to: diagonal });
    }
  }
};

const chebyshev = (a: number, b: number): number =>
  Math.max(Math.abs(fileOf(a) - fileOf(b)), Math.abs(rankOf(a) - rankOf(b)));

/** Conditions on the mover that cut moves after the abilities offered them. */
const filterByCondition = (
  position: Position,
  from: number,
  mover: Piece,
  side: Side,
  moves: Move[],
) => {
  let result = moves;
  if (isSlow(position.rules, from))
    result = result.filter((move) => chebyshev(from, move.to) === 1);
  if (mover.backwardTurns > 0) {
    const away = -forwardDirection(side);
    result = result.filter((move) => Math.sign(rankOf(move.to) - rankOf(from)) === away);
  }
  return result;
};

export const movesForPiece = (position: Position, from: number): Move[] => {
  const mover = position.board[from];
  const out: Move[] = [];
  if (!mover || !isSide(mover.owner) || mover.stoppedTurns > 0 || mover.heldBy) return out;

  for (const ability of effectiveAbilities(mover)) {
    switch (ability) {
      case 'pawn':
        addPawnMoves(position, from, mover, mover.owner, out);
        break;
      case 'knight':
        addLeaps(position, from, mover, KNIGHT_OFFSETS, out);
        break;
      case 'king':
        addLeaps(position, from, mover, KING_OFFSETS, out);
        break;
      case 'hop':
        addLeaps(position, from, mover, HOP_OFFSETS, out);
        break;
      case 'bishop':
        addSlides(position, from, mover, BISHOP_DIRECTIONS, out);
        break;
      case 'rook':
        addSlides(position, from, mover, ROOK_DIRECTIONS, out);
        break;
      case 'cannon':
        addCannon(position, from, mover, out);
        break;
    }
  }
  const moves = filterByCondition(position, from, mover, mover.owner, dedupeMoves(out));
  // A push is not a capture, so a piece that pushes still moves into a piece.
  return position.rules.noCapturePlies > 0
    ? moves.filter((move) => !isCapture(position, move))
    : moves;
};

/** A piece with two abilities can reach one square two ways. Keep one. */
const dedupeMoves = (moves: Move[]): Move[] => {
  const seen = new Set<number>();
  return moves.filter((move) => {
    if (seen.has(move.to)) return false;
    seen.add(move.to);
    return true;
  });
};

/** A move into an occupied square captures, unless it pushes the occupant. */
export const isCapture = (position: Position, move: Move): boolean =>
  position.board[move.to] !== null && pushTarget(position, move) === null;

/**
 * Every legal move for a side. A mustCapture piece that can capture forces the
 * side to capture with it.
 */
export const generateMoves = (position: Position, side: Side): Move[] => {
  const out: Move[] = [];
  const forced: Move[] = [];
  position.board.forEach((piece, square) => {
    if (piece?.owner !== side) return;
    const moves = movesForPiece(position, square);
    out.push(...moves);
    if (piece.mustCapture) forced.push(...moves.filter((move) => isCapture(position, move)));
  });
  return forced.length > 0 ? forced : out;
};

/** Squares of mustCapture pieces that can capture now. They force the side's move. */
export const hungryMovers = (position: Position, side: Side): number[] => {
  const result: number[] = [];
  position.board.forEach((piece, square) => {
    if (piece?.owner !== side || !piece.mustCapture) return;
    if (movesForPiece(position, square).some((move) => isCapture(position, move))) {
      result.push(square);
    }
  });
  return result;
};

export const isSquareAttacked = (position: Position, square: number, bySide: Side): boolean =>
  generateMoves(position, bySide).some((move) => move.to === square && isCapture(position, move));

export const isMoveLegal = (position: Position, move: Move, side: Side): boolean =>
  generateMoves(position, side).some(
    (candidate) => candidate.from === move.from && candidate.to === move.to,
  );

export const rulesWithoutTerrain = (rules: Rules): Rules => ({ ...rules, terrain: {} });
